<template>
  <Modal ref="modal" @show="cargar">
    <h2 class="box__title extras-mg-permisos__titulo">
      Permisos de «{{ datos?.campo?.nombre || field.name }}»
    </h2>
    <p class="extras-mg-permisos__sub">
      Quién puede escribir en esta columna. Quien no puede la ve en solo
      lectura, y tampoco la cambia ni la borra.
    </p>

    <Error :error="error"></Error>

    <div v-if="cargando && !datos" class="loading"></div>

    <form v-else-if="datos" @submit.prevent="guardar">
      <div class="extras-mg-permisos__niveles">
        <Radio
          v-for="n in niveles"
          :key="n"
          v-model="nivel"
          :value="n"
          :disabled="!datos.yo.niveles.includes(n)"
          class="extras-mg-permisos__nivel"
        >
          <span class="extras-mg-permisos__nombre">{{ nombres[n] }}</span>
          <span class="extras-mg-permisos__descripcion">
            {{ descripciones[n] }}
            <template v-if="!datos.yo.niveles.includes(n)">
              Lo pone un admin.
            </template>
          </span>
        </Radio>
      </div>

      <Checkbox
        v-model="formularios"
        :disabled="nivel === EDITOR"
        class="extras-mg-permisos__formularios"
      >
        Se puede llenar desde formularios
      </Checkbox>
      <p class="extras-mg-permisos__ayuda">
        {{
          nivel === EDITOR
            ? 'Sin restricción, la columna va en los formularios como cualquier otra.'
            : 'Si no se marca, no aparece en ningún formulario, ni en los públicos.'
        }}
      </p>

      <div class="extras-mg-permisos__acciones">
        <Button
          type="primary"
          :loading="guardando"
          :disabled="guardando || sinCambios"
        >
          Guardar
        </Button>
      </div>
    </form>
  </Modal>
</template>

<script>
/**
 * "Permisos de la columna": quién escribe sus valores (extras_mg/api/campos).
 *
 * El servidor dice qué niveles puede poner esta persona (yo.niveles); los que
 * no, se ven apagados. Al guardar se piden los permisos otra vez para que la
 * cuadrícula quede al día sin recargar; a los demás navegadores les llega el
 * aviso en vivo 'mg_permisos_de_campo'.
 */
import modal from '@baserow/modules/core/mixins/modal'
import error from '@baserow/modules/core/mixins/error'
import CamposService from '@extras_mg/services/campos'
import {
  DESCRIPCIONES_DE_NIVEL,
  EDITOR,
  NIVELES,
  NOMBRES_DE_NIVEL,
} from '@extras_mg/utils/roles'

export default {
  name: 'PermisosDeColumnaModal',
  mixins: [modal, error],
  props: {
    field: { type: Object, required: true },
    workspaceId: { type: Number, required: true },
  },
  data() {
    return {
      EDITOR,
      niveles: NIVELES,
      nombres: NOMBRES_DE_NIVEL,
      descripciones: DESCRIPCIONES_DE_NIVEL,
      datos: null,
      cargando: false,
      guardando: false,
      nivel: EDITOR,
      formularios: false,
    }
  },
  computed: {
    sinCambios() {
      return (
        this.nivel === this.datos?.nivel &&
        (this.nivel === EDITOR ||
          this.formularios === this.datos?.permitir_en_formularios)
      )
    },
  },
  methods: {
    /** El servidor contesta con un `detail` ya en español: se enseña ese. */
    mostrarError(e) {
      const detalle = e?.response?.data?.detail
      if (detalle && typeof detalle === 'string') {
        e.handler?.handled?.()
        this.showError('No se pudo', detalle)
      } else {
        this.handleError(e)
      }
    },
    tomar(datos) {
      this.datos = datos
      this.nivel = datos.nivel
      this.formularios = datos.permitir_en_formularios
    },
    async cargar() {
      this.cargando = true
      this.datos = null
      this.hideError()
      try {
        const { data } = await CamposService(this.$client).obtener(this.field.id)
        this.tomar(data)
      } catch (e) {
        this.mostrarError(e)
      }
      this.cargando = false
    },
    async guardar() {
      this.guardando = true
      this.hideError()
      try {
        const { data } = await CamposService(this.$client).guardar(
          this.field.id,
          this.nivel,
          this.nivel !== EDITOR && this.formularios
        )
        this.tomar(data)
        const workspace = this.$store.getters['workspace/get'](this.workspaceId)
        if (workspace) {
          await this.$store.dispatch('workspace/forceFetchPermissions', workspace)
        }
        this.$store.dispatch('toast/success', {
          title: 'Permisos guardados',
          message: `«${data.campo.nombre}»: ${NOMBRES_DE_NIVEL[data.nivel]}.`,
        })
        this.hide()
      } catch (e) {
        this.mostrarError(e)
      }
      this.guardando = false
    },
  },
}
</script>

<style lang="scss">
/* Sin colores fijos: build-theme.py invierte cualquier hex del CSS compilado,
   así que todo sale de currentColor y opacidades y sirve en claro y oscuro. */
.extras-mg-permisos__titulo {
  margin-bottom: 4px;
  overflow-wrap: anywhere;
}

.extras-mg-permisos__sub {
  font-size: 13px;
  margin: 0 0 16px;
  opacity: 0.7;
}

.extras-mg-permisos__niveles {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 18px;
}

.extras-mg-permisos__nivel {
  align-items: flex-start;
}

/* Pegado a su botón: sin esto el texto salía centrado a lo ancho del modal,
   lejos del círculo que lo elige. */
.extras-mg-permisos__nivel .radio__label {
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: 2px;
  text-align: left;
}

.extras-mg-permisos__nombre {
  font-size: 14px;
  font-weight: 500;
}

.extras-mg-permisos__descripcion,
.extras-mg-permisos__ayuda {
  font-size: 12px;
  opacity: 0.65;
}

.extras-mg-permisos__ayuda {
  margin: 6px 0 0;
}

.extras-mg-permisos__acciones {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
}
</style>
